import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api.js';
import { useAuth } from '../context/AuthContext';
import { useRef } from 'react';
import {
  fieldLabelsDomainDetails,
  isDateField,
  isTagsField,
  isRiskScoreField,
  isComputerTypeField,
  getRiskScoreLabel,
  getTextColorForBackground,
  getRiskScoreColor,
  formatDate,
  getComputerTypeOptions,
  getUserRiskScore,
  getUserComputerType
} from '../components/utils';
import { ArrowDownIcon } from '../components/Icons';
import VirtualizedTableBody from '../components/VirtualizedTableBody';

// Findings table for a monitored URL (/urls/:url) or slug (/slugs/:slug, kind="slug")
const UrlDetails = ({ kind = 'url' }) => {
  const { user } = useAuth();
  const params = useParams();
  const isSlug = kind === 'slug';
  const url = isSlug ? params.slug : params.url;
  const targetLabel = isSlug ? 'slug' : 'URL';
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rowUpdateError, setRowUpdateError] = useState('');
  const [sortField, setSortField] = useState('added');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filters, setFilters] = useState({});
  const [tagsOptions, setTagsOptions] = useState([]);
  const [showTagsDropdown, setShowTagsDropdown] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({});
  const [datePopover, setDatePopover] = useState({ field: null, anchor: null });
  const [hoveredEye, setHoveredEye] = useState(null);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [pendingSolvedUpdates, setPendingSolvedUpdates] = useState({});
  const debounceTimer = useRef(null);
  const pendingSolvedUpdatesRef = useRef(pendingSolvedUpdates);

  // --- Risk Score Batch Update State ---
  const [pendingRiskScoreUpdates, setPendingRiskScoreUpdates] = useState({});
  const riskScoreDebounceTimer = useRef(null);
  const pendingRiskScoreUpdatesRef = useRef(pendingRiskScoreUpdates);

  // --- Computer Type Batch Update State ---
  const [pendingComputerTypeUpdates, setPendingComputerTypeUpdates] = useState({});
  const computerTypeDebounceTimer = useRef(null);
  const pendingComputerTypeUpdatesRef = useRef(pendingComputerTypeUpdates);

  useEffect(() => {
    fetchUrlDetails();
    fetchTags();
    // eslint-disable-next-line
  }, [url]);

  const fetchUrlDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const data = isSlug ? await api.getSlugDetails(url) : await api.getUrlDetails(url);
      setRecords(data.results || []);
    } catch (err) {
      if (err.message && err.message.toLowerCase().includes('access denied')) {
        setError(`You are not monitoring this ${targetLabel} or do not have access.`);
      } else {
        setError(`Failed to load ${targetLabel} details`);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const data = await api.getTags();
      // Normalize tags to objects with id, name, color, priority, etc. (id as string)
      setTagsOptions(
        (data.tags || []).map(tagArr => ({
          id: String(tagArr.id),
          name: tagArr.name,
          color: tagArr.color,
          priority: tagArr.priority,
        }))
      );
    } catch (err) {
      // ignore
    }
  };

  // Filtering logic
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateFilterChange = (field, bound, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [bound]: value
      }
    }));
  };

  const handleTagsFilterChange = (tagId) => {
    const tagIdStr = String(tagId);
    setFilters((prev) => {
      const prevTags = (prev.tags || []).map(String);
      if (prevTags.includes(tagIdStr)) {
        return { ...prev, tags: prevTags.filter((id) => id !== tagIdStr) };
      } else {
        return { ...prev, tags: [...prevTags, tagIdStr] };
      }
    });
  };

  // Sorting logic
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Password visibility logic
  const togglePasswordVisibility = (idx) => {
    setPasswordVisibility((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Helper function to get masked value for search comparison
  const getMaskedValueForSearch = (field, value, userCredentialVisibility, passwordVisibility = {}) => {
    if (!value) return '';
    
    if (field === 'password') {
      if (!userCredentialVisibility || !passwordVisibility) {
        return `${'*'.repeat(Math.max(0, value.length - 3))}${value.slice(-3)}`;
      }
      return value;
    }
    
    if (field === 'user') {
      if (!userCredentialVisibility) {
        const atIndex = value.indexOf('@');
        if (atIndex === -1 || atIndex <= 5) {
          // If no @ or @ is at position 5 or less, mask all but last 3
          return `${'*'.repeat(Math.max(0, value.length - 3))}${value.slice(-3)}`;
        } else {
          // Mask last 5 characters before @
          const beforeAt = value.substring(0, atIndex);
          const afterAt = value.substring(atIndex);
          const visiblePart = beforeAt.substring(0, Math.max(0, beforeAt.length - 5));
          const maskedPart = '*'.repeat(Math.min(5, beforeAt.length));
          return `${visiblePart}${maskedPart}${afterAt}`;
        }
      }
      return value;
    }
    
    return value;
  };

  // Filtering and sorting records
  const filteredRecords = useMemo(() => {
    let filtered = [...records];
    Object.entries(filters).forEach(([field, value]) => {
      if (isDateField(field)) {
        if (value?.from) {
          filtered = filtered.filter((rec) => rec[field] && rec[field] >= value.from);
        }
        if (value?.to) {
          filtered = filtered.filter((rec) => rec[field] && rec[field] <= value.to);
        }
      } else if (isTagsField(field)) {
        if (value && value.length > 0) {
          const valueStrs = value.map(String);
          filtered = filtered.filter((rec) => {
            if (!Array.isArray(rec.tags)) return false;
            // Compare as strings
            return rec.tags.map(String).some((tag) => valueStrs.includes(tag));
          });
        }
      } else if (isRiskScoreField(field)) {
        if (value) {
          const score = parseInt(value);
          if (!isNaN(score)) {
            filtered = filtered.filter((rec) => getUserRiskScore(rec[field], user?.id) === score);
          }
        }
      } else if (isComputerTypeField(field)) {
        if (value) {
          filtered = filtered.filter((rec) => getUserComputerType(rec[field], user?.id) === value);
        }
      } else if (value) {
        filtered = filtered.filter((rec) => {
          // For password and user fields, use masked values for search comparison
          const searchValue = getMaskedValueForSearch(field, rec[field], user?.credential_visibility, passwordVisibility);
          return searchValue.toLowerCase().includes(value.toLowerCase());
        });
      }
    });
    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (isDateField(sortField)) {
        aVal = aVal || '';
        bVal = bVal || '';
        if (sortOrder === 'asc') return aVal.localeCompare(bVal);
        return bVal.localeCompare(aVal);
      } else if (isTagsField(sortField)) {
        aVal = (aVal || []).map(String).join(',');
        bVal = (bVal || []).map(String).join(',');
        if (sortOrder === 'asc') return aVal.localeCompare(bVal);
        return bVal.localeCompare(aVal);
      } else if (isRiskScoreField(sortField)) {
        aVal = getUserRiskScore(aVal, user?.id) || 0;
        bVal = getUserRiskScore(bVal, user?.id) || 0;
        if (sortOrder === 'asc') return aVal - bVal;
        return bVal - aVal;
      } else if (isComputerTypeField(sortField)) {
        aVal = getUserComputerType(aVal, user?.id) || '';
        bVal = getUserComputerType(bVal, user?.id) || '';
        if (sortOrder === 'asc') return aVal.localeCompare(bVal);
        return bVal.localeCompare(aVal);
      } else {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
        if (sortOrder === 'asc') return aVal.localeCompare(bVal);
        return bVal.localeCompare(aVal);
      }
    });
    return filtered;
  }, [records, filters, sortField, sortOrder]);

  // Close tags dropdown on outside click
  React.useEffect(() => {
    if (!showTagsDropdown) return;
    const handleClick = (e) => {
      // Close if clicking outside the dropdown or on the button that opened it
      if (!e.target.closest('.tags-dropdown') && !e.target.closest('.tags-button')) {
        setShowTagsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showTagsDropdown]);

  // Close date popover on outside click
  React.useEffect(() => {
    if (!datePopover.field) return;
    const handleClick = (e) => {
      // Close if clicking outside the popover or on the button that opened it
      if (!e.target.closest('.date-popover') && !e.target.closest('.date-button')) {
        setDatePopover({ field: null, anchor: null });
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [datePopover]);

  // Keep the ref updated with the latest pending updates
  useEffect(() => {
    pendingSolvedUpdatesRef.current = pendingSolvedUpdates;
  }, [pendingSolvedUpdates]);

  useEffect(() => {
    pendingRiskScoreUpdatesRef.current = pendingRiskScoreUpdates;
  }, [pendingRiskScoreUpdates]);

  useEffect(() => {
    pendingComputerTypeUpdatesRef.current = pendingComputerTypeUpdates;
  }, [pendingComputerTypeUpdates]);

  // Tag id to name map
  const tagIdToName = useMemo(() => {
    const map = {};
    tagsOptions.forEach((tag) => {
      map[String(tag.id)] = tag.name;
    });
    return map;
  }, [tagsOptions]);

  // Add helper for tag color lookup
  const getTagColor = (tagId) => {
    const tagIdStr = String(tagId);
    for (const tag of tagsOptions) {
      if (String(tag.id) === tagIdStr) {
        return tag.color;
      }
    }
    return '#e5e7eb';
  };

  // Helper to get tag priority
  const getTagPriority = (tagId) => {
    const tagIdStr = String(tagId);
    const tag = tagsOptions.find(t => String(t.id) === tagIdStr);
    return tag ? tag.priority : 0;
  };

  // Handle solved checkbox toggle
  const handleSolvedToggle = (recordId, checked) => {
    setRecords(prevData =>
      prevData.map(rec =>
        rec.id === recordId
          ? {
              ...rec,
              solved: checked
                ? [...(rec.solved || []), user.id]
                : (rec.solved || []).filter(uid => uid !== user.id),
            }
          : rec
      )
    );
    setPendingSolvedUpdates(prev => ({
      ...prev,
      [recordId]: checked,
    }));
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      sendBatchSolvedUpdates();
    }, 2000);
  };

  const handleRiskScoreChange = (recordId, newScore) => {
    setRecords(prevData =>
      prevData.map(rec =>
        rec.id === recordId
          ? {
              ...rec,
              risk_score: (() => {
                let arr = Array.isArray(rec.risk_score)
                  ? [...rec.risk_score]
                  : (typeof rec.risk_score === 'number'
                      ? [{ user_id: 0, risk_score: rec.risk_score }]
                      : []);
                const idx = arr.findIndex(e => e.user_id === user?.id);
                if (idx !== -1) {
                  arr[idx] = { user_id: user?.id, risk_score: newScore };
                } else {
                  arr.push({ user_id: user?.id, risk_score: newScore });
                }
                return arr;
              })()
            }
          : rec
      )
    );
    setPendingRiskScoreUpdates(prev => ({
      ...prev,
      [recordId]: newScore,
    }));
    if (riskScoreDebounceTimer.current) clearTimeout(riskScoreDebounceTimer.current);
    riskScoreDebounceTimer.current = setTimeout(() => {
      sendBatchRiskScoreUpdates();
    }, 2000);
  };

  // --- Computer Type Change Handler ---
  const handleComputerTypeChange = (recordId, newType, userId) => {
    setRecords(prevData => {
      // Find the record being updated to get its source
      const updatedRecord = prevData.find(rec => rec.id === recordId);
      if (!updatedRecord) return prevData;
      
      const source = updatedRecord.source;
      
      // Update the specific record and all other records with the same source
      return prevData.map(rec =>
        (rec.id === recordId || (source && rec.source === source))
          ? { ...rec, computer_type: newType }
          : rec
      );
    });
    setPendingComputerTypeUpdates(prev => ({
      ...prev,
      [recordId]: { computer_type: newType, user_id: userId },
    }));
    if (computerTypeDebounceTimer.current) clearTimeout(computerTypeDebounceTimer.current);
    computerTypeDebounceTimer.current = setTimeout(() => {
      sendBatchComputerTypeUpdates();
    }, 2000);
  };

  // Tag management handlers
  const handleTagAdded = (recordId, tagId) => {
    setRecords(prevData =>
      prevData.map(rec =>
        rec.id === recordId
          ? {
              ...rec,
              tags: (() => {
                const newTags = [...(rec.tags || []), String(tagId)];
                // Sort by priority (highest first), then alphabetically
                return newTags.sort((a, b) => {
                  const tagA = tagsOptions.find(tag => String(tag.id) === a);
                  const tagB = tagsOptions.find(tag => String(tag.id) === b);
                  const priorityA = tagA?.priority || 0;
                  const priorityB = tagB?.priority || 0;
                  
                  if (priorityA !== priorityB) {
                    return priorityB - priorityA; // Higher priority first
                  }
                  // If same priority, sort alphabetically by name
                  return (tagA?.name || '').localeCompare(tagB?.name || '');
                });
              })()
            }
          : rec
      )
    );
  };

  const handleTagRemoved = (recordId, tagId) => {
    setRecords(prevData =>
      prevData.map(rec =>
        rec.id === recordId
          ? {
              ...rec,
              tags: (rec.tags || []).filter(tag => tag !== String(tagId))
            }
          : rec
      )
    );
  };

  const sendBatchSolvedUpdates = async () => {
    const updatesObj = pendingSolvedUpdatesRef.current;
    if (Object.keys(updatesObj).length === 0) return;
    const updates = Object.entries(updatesObj).map(([id, checked]) => ({
      id,
      checked,
    }));
    try {
      await api.updateSolvedBatch(updates);
      setPendingSolvedUpdates({});
    } catch (err) {
      setRowUpdateError('Failed to update solved status');
      console.error('Failed to update solved status', err);
    }
  };

  const sendBatchRiskScoreUpdates = async () => {
    const updatesObj = pendingRiskScoreUpdatesRef.current;
    if (Object.keys(updatesObj).length === 0) return;
    const updates = Object.entries(updatesObj).map(([id, score]) => ({
      id,
      score,
      user_id: user?.id,
    }));
    try {
      await api.updateRiskScoreBatch(updates);
      setPendingRiskScoreUpdates({});
    } catch (err) {
      setRowUpdateError('Failed to update risk scores');
      console.error('Failed to update risk scores', err);
    }
  };

  const sendBatchComputerTypeUpdates = async () => {
    const updatesObj = pendingComputerTypeUpdatesRef.current;
    if (Object.keys(updatesObj).length === 0) return;
    const updates = Object.entries(updatesObj).map(([id, { computer_type, user_id }]) => ({
      id,
      computer_type,
      user_id: user_id,
    }));
    try {
      await api.updateComputerTypeBatch(updates);
      setPendingComputerTypeUpdates({});
    } catch (err) {
      setRowUpdateError('Failed to update computer types');
      console.error('Failed to update computer types', err);
    }
  };

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        if (Object.keys(pendingSolvedUpdatesRef.current).length > 0) {
          sendBatchSolvedUpdates();
        }
      }
      if (riskScoreDebounceTimer.current) {
        clearTimeout(riskScoreDebounceTimer.current);
        if (Object.keys(pendingRiskScoreUpdatesRef.current).length > 0) {
          sendBatchRiskScoreUpdates();
        }
      }
      if (computerTypeDebounceTimer.current) {
        clearTimeout(computerTypeDebounceTimer.current);
        if (Object.keys(pendingComputerTypeUpdatesRef.current).length > 0) {
          sendBatchComputerTypeUpdates();
        }
      }
    };
  }, []);

  // UI
  return (
    <>
      <style>
        {`
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #6b7280 #374151;
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #374151;
            border-radius: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #6b7280;
            border-radius: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #9ca3af;
          }
          .custom-scrollbar::-webkit-scrollbar-corner {
            background: #374151;
          }
        `}
      </style>
      <div className="container" style={{ width: '100%', padding: '1.5rem 3vw' }}>
        {rowUpdateError && (
          <div style={{ background: 'var(--danger, #e57373)', color: '#fff', padding: '10px 16px', borderRadius: 6, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 15, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <span>{rowUpdateError}</span>
            <button style={{ background: 'none', border: 'none', color: '#fff', fontWeight: 700, fontSize: 18, cursor: 'pointer', marginLeft: 12, lineHeight: 1 }} onClick={() => setRowUpdateError('')} title="Dismiss error">×</button>
          </div>
        )}
        <div style={{ marginBottom: '1.2rem' }}>
          <h1 style={{ color: 'var(--text)', margin: 0, fontSize: '1.3rem' }}>{isSlug ? 'Slug' : 'URL'}: {url}</h1>
        </div>
        {error ? (
          <div className="error-message" style={{ color: 'var(--danger)', padding: '1.2rem', textAlign: 'center', fontSize: '1rem' }}>{error}</div>
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: '1.2rem', color: 'var(--text-light)', fontSize: '1rem' }}>Loading...</div>
        ) : (
          <div style={{
            background: 'var(--white)',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.07)',
            border: '1px solid var(--border)',
            overflow: 'visible',
            marginBottom: '1.2rem'
          }}>
            <div style={{ padding: '0.5rem 0.5rem 0.5rem 0.5rem', borderBottom: '1px solid var(--border)', background: 'var(--background)', display: 'flex', flexWrap: 'wrap', gap: '0.7rem' }}>
              {Object.keys(fieldLabelsDomainDetails).map((field) => (
                <div key={field} style={{ minWidth: 90, maxWidth: 160, position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                  {isDateField(field) ? (
                    <>
                      <button
                        type="button"
                        className="form-input date-button"
                        style={{
                          minWidth: 90,
                          fontSize: 12,
                          padding: '0.1rem 0.4rem',
                          cursor: 'pointer',
                          background: '#374151',
                          color: 'var(--text)',
                          textAlign: 'left',
                          height: 28,
                          border: '2px solid var(--border)',
                          borderRadius: 8,
                          marginTop: 0,
                          marginBottom: 0,
                          boxSizing: 'border-box',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        onClick={e => {
                          if (datePopover.field === field) {
                            setDatePopover({ field: null, anchor: null });
                          } else {
                            setDatePopover({ field, anchor: e.currentTarget });
                          }
                        }}
                      >
                        {filters[field]?.from || filters[field]?.to
                          ? `${filters[field]?.from || '...'} ~ ${filters[field]?.to || '...'}`
                          : `Any ${fieldLabelsDomainDetails[field]}`}
                      </button>
                      {datePopover.field === field && (
                        <div className="date-popover" style={{
                          position: 'absolute',
                          zIndex: 1000,
                          background: '#374151',
                          border: '1px solid var(--border)',
                          borderRadius: 6,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          top: '100%',
                          left: 0,
                          minWidth: '100%',
                          padding: '0.7rem',
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <label style={{ fontWeight: 500, color: 'var(--text-light)', fontSize: 11 }}>From</label>
                            <input type="date" className="form-input" style={{ fontSize: 12, padding: '0.1rem 0.4rem', height: 28 }} value={filters[field]?.from || ''} onChange={e => handleDateFilterChange(field, 'from', e.target.value)} />
                            <label style={{ fontWeight: 500, color: 'var(--text-light)', fontSize: 11 }}>To</label>
                            <input type="date" className="form-input" style={{ fontSize: 12, padding: '0.1rem 0.4rem', height: 28 }} value={filters[field]?.to || ''} onChange={e => handleDateFilterChange(field, 'to', e.target.value)} />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 6 }}>
                              <button className="btn btn-link" style={{ fontSize: 11 }} onClick={() => { setFilters(f => ({ ...f, [field]: {} })); setDatePopover({ field: null, anchor: null }); }}>Clear</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : isTagsField(field) ? (
                    <div style={{ position: 'relative' }}>
                      <label style={{ fontWeight: 500, color: 'var(--text-light)', fontSize: 11 }}>{fieldLabelsDomainDetails[field]}</label>
                      <div
                        className="form-input tags-button"
                        style={{
                          minWidth: 90,
                          fontSize: 12,
                          padding: '0.1rem 0.4rem',
                          cursor: 'pointer',
                          userSelect: 'none',
                          background: '#374151',
                          color: 'var(--text)',
                          height: 28,
                          border: '2px solid var(--border)',
                          borderRadius: 8,
                          marginTop: 0,
                          marginBottom: 0,
                          boxSizing: 'border-box',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                        onClick={() => setShowTagsDropdown((v) => !v)}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {filters.tags && filters.tags.length > 0
                            ? filters.tags
                                .filter((id) => tagIdToName[id])
                                .map((id) => tagIdToName[id])
                                .join(', ') || 'All'
                            : 'All'}
                        </span>
                        <ArrowDownIcon />
                      </div>
                      {showTagsDropdown && (
                        <div className="tags-dropdown" style={{ position: 'absolute', zIndex: 1000, background: '#374151', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.3)', top: '100%', left: 0, minWidth: '100%' }}>
                          {tagsOptions.map((tag) => (
                            <label key={tag.id} style={{ display: 'flex', alignItems: 'center', padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: 12 }}>
                              <input
                                type="checkbox"
                                checked={filters.tags && filters.tags.map(String).includes(String(tag.id))}
                                onChange={() => handleTagsFilterChange(tag.id)}
                                style={{ marginRight: 6 }}
                              />
                              <span>{tag.name}</span>
                            </label>
                          ))}

                        </div>
                      )}
                    </div>
                  ) : isRiskScoreField(field) ? (
                    <div>
                      <label style={{ fontWeight: 500, color: 'var(--text-light)', fontSize: 11 }}>{fieldLabelsDomainDetails[field]}</label>
                      <select
                        className="form-input"
                        style={{ minWidth: 90, fontSize: 12, padding: '0.1rem 0.4rem', height: 28 }}
                        value={filters[field] || ''}
                        onChange={e => handleFilterChange(field, e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="1">Low</option>
                        <option value="2">Medium</option>
                        <option value="3">High</option>
                        <option value="4">Very High</option>
                        <option value="5">Critical</option>
                      </select>
                    </div>
                  ) : field === 'computer_type' ? (
                    <div>
                      <label style={{ fontWeight: 500, color: 'var(--text-light)', fontSize: 11 }}>{fieldLabelsDomainDetails[field]}</label>
                      <select
                        className="form-input"
                        style={{ minWidth: 90, fontSize: 12, padding: '0.1rem 0.4rem', height: 28 }}
                        value={filters[field] || ''}
                        onChange={e => handleFilterChange(field, e.target.value)}
                      >
                        <option value="">All</option>
                        {getComputerTypeOptions().map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div>
                      <label style={{ fontWeight: 500, color: 'var(--text-light)', fontSize: 11 }}>{fieldLabelsDomainDetails[field]}</label>
                      <input
                        type="text"
                        className="form-input"
                        style={{ minWidth: 90, fontSize: 12, padding: '0.1rem 0.4rem', height: 28 }}
                        value={filters[field] || ''}
                        onChange={e => handleFilterChange(field, e.target.value)}
                        placeholder={`Search ${fieldLabelsDomainDetails[field]}`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="custom-scrollbar" style={{ overflowX: 'hidden' }}>
              <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.92rem', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', boxShadow: '0 2px 8px rgba(30,39,56,0.04)' }}>
                <thead>
                  <tr style={{ background: 'var(--background)', display: 'flex', width: '100%' }}>
                    {/* Checkbox header for solved status */}
                    <th style={{ width: 36, minWidth: 36, maxWidth: 36, background: 'var(--background)', borderBottom: '2px solid var(--border)', textAlign: 'center', padding: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className="fas fa-clipboard-check" style={{ color: 'var(--text)', fontSize: '1rem' }} title="Solved Status"></i>
                    </th>
                    {Object.keys(fieldLabelsDomainDetails).map((field) => (
                      <th
                        key={field}
                        className={
                          field === 'computer_type' ? 'col-computer_type' :
                          field === 'date' ? 'col-date' :
                          field === 'bucket' ? 'col-bucket' : ''
                        }
                        style={{
                          textAlign: 'left',
                          padding: '0.7rem',
                          color: 'var(--text)',
                          fontWeight: 700,
                          cursor: 'pointer',
                          userSelect: 'none',
                          fontSize: '1rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          background: 'var(--background)',
                          borderBottom: '2px solid var(--border)',
                          flex: isTagsField(field) ? 0.6 : 1,
                          maxWidth: isTagsField(field) ? '200px' : 'none'
                        }}
                        onClick={() => handleSort(field)}
                      >
                        {fieldLabelsDomainDetails[field]}
                        {sortField === field && (
                          <span style={{ marginLeft: 3, color: 'var(--primary-color)' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                        )}
                      </th>
                    ))}
                    <th key="intelx-link" style={{ width: 36, minWidth: 36, maxWidth: 36, borderBottom: '2px solid var(--border)', background: 'var(--background)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}></th>
                  </tr>
                </thead>
              </table>
              {filteredRecords.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '1.2rem', color: 'var(--text-light)', fontSize: '0.95rem', background: '#374151' }}>No records found.</div>
              ) : (
                <VirtualizedTableBody
                  records={filteredRecords}
                  passwordVisibility={passwordVisibility}
                  hoveredLink={hoveredLink}
                  hoveredEye={hoveredEye}
                  tagIdToName={tagIdToName}
                  getTagColor={getTagColor}
                  getTagPriority={getTagPriority}
                  formatDate={formatDate}
                  getRiskScoreLabel={getRiskScoreLabel}
                  getRiskScoreColor={getRiskScoreColor}
                  getTextColorForBackground={getTextColorForBackground}
                  isDateField={isDateField}
                  isTagsField={isTagsField}
                  isRiskScoreField={isRiskScoreField}
                  fieldLabelsDashboard={fieldLabelsDomainDetails}
                  onTogglePasswordVisibility={togglePasswordVisibility}
                  onHoveredLink={setHoveredLink}
                  onHoveredEye={setHoveredEye}
                  currentUserId={user?.id}
                  onSolvedToggle={handleSolvedToggle}
                  onRiskScoreChange={handleRiskScoreChange}
                  onComputerTypeChange={handleComputerTypeChange}
                  getUserRiskScore={getUserRiskScore}
                  userCredentialVisibility={user?.credential_visibility}
                  tagsOptions={tagsOptions}
                  onTagAdded={handleTagAdded}
                  onTagRemoved={handleTagRemoved}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UrlDetails;


